import * as React from 'react';
import config from '../config/config';
import { Card, CardHeader, CardTitle, CardActions, CardSubtitle, Avatar } from '@progress/kendo-react-layout';
import { Button } from '@progress/kendo-react-buttons';
import { Dialog } from '@progress/kendo-react-dialogs';
import { trashIcon, pencilIcon } from '@progress/kendo-svg-icons';
import { useNavigate } from 'react-router-dom';
import { Loader } from '@progress/kendo-react-indicators';
import { createGlobalStyle } from 'styled-components';

/* ------------------------------------------------------------------ */
/* Delete-Modal im neuen Stil                                         */
/* ------------------------------------------------------------------ */
const GlobalStyle = createGlobalStyle`
  .delete-confirmation-modal {
    .k-dialog-titlebar {
      justify-content: space-between;
      align-items: center;
    }
    .k-button:not(.k-dialog-titlebar-action):not(.k-window-titlebar-action) {
      border-radius: 8px;
      min-width: 90px;
    }
  }
`;

const DeleteConfirmationModal = ({
  visible,
  title = 'Löschen bestätigen',
  onConfirm,
  onCancel,
  children
}) => {
  if (!visible) return null;
  return (
    <>
      <GlobalStyle />
      <Dialog title={title} onClose={onCancel} width={420} className="delete-confirmation-modal">
        <div style={{ padding: 10 }}>
          <p style={{ margin: '20px 0', textAlign: 'center', lineHeight: 1.4 }}>
            {children}
          </p>
          <div style={{ display: 'flex', justifyContent: 'center', gap: 10, marginTop: 20 }}>
            <Button onClick={onCancel}>Abbrechen</Button>
            <Button themeColor="primary" onClick={onConfirm}>Löschen</Button>
          </div>
        </div>
      </Dialog>
    </>
  );
};

const Users = () => {
  const [users, setUsers] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [visibleDialog, setVisibleDialog] = React.useState(false);
  const [selectedUser, setSelectedUser] = React.useState(null);
  const navigate = useNavigate();

  React.useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await fetch(`${config.host}/users`);
        const data = await response.json();
        data.sort((a, b) => {
          const nameA = `${a.Vorname} ${a.Nachname}`.toLowerCase();
          const nameB = `${b.Vorname} ${b.Nachname}`.toLowerCase();
          return nameA.localeCompare(nameB, 'de');
        });
        setUsers(data);
      } catch (error) {
        console.error('Fehler beim Laden der Benutzer:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchUsers();
  }, []);

  const handleDelete = (user) => {
    setSelectedUser(user);
    setVisibleDialog(true);
  };

  const handleConfirmDelete = async () => {
    if (!selectedUser) return;
    try {
      const response = await fetch(`${config.host}/users/${selectedUser.BenutzerID}`, {
        method: 'DELETE'
      });
      if (!response.ok) throw new Error('Fehler beim Löschen des Benutzers');
      setUsers(users.filter(u => u.BenutzerID !== selectedUser.BenutzerID));
    } catch (error) {
      console.error(error);
    } finally {
      setVisibleDialog(false);
      setSelectedUser(null);
    }
  };

  const handleCancelDelete = () => {
    setVisibleDialog(false);
    setSelectedUser(null);
  };

  const handleEdit = (user) => navigate(`/edit-users/${user.BenutzerID}`);
  const handleAddUser = () => navigate('/create-users');

  return (
    <div id="Users" className="profile-page main-content">
      <div className="card-container">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <h3 className="card-title">Benutzerverwaltung</h3>
          <Button themeColor="primary" onClick={handleAddUser}>Nutzer hinzufügen</Button>
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', marginTop: 20 }}>
            <Loader size="large" type="infinite-spinner" />
            <p>Benutzer werden geladen...</p>
          </div>
        ) : (
          <div className="card-component" style={{ display: 'flex', justifyContent: 'space-evenly', flexWrap: 'wrap' }}>
            {users.map((user, index) => (
              <div key={index}>
                <Card style={{ width: 260, boxShadow: '0 0 4px 0 rgba(0, 0, 0, .1)', marginTop: 15 }}>
                  <CardHeader className="k-hbox" style={{ background: 'transparent' }}>
                    <Avatar type="image" size="medium" rounded="full">
                      <img
                        style={{ width: 45, height: 45 }}
                        alt="User Avatar"
                        src={`${config.host}/get_image/${user.AvatarThumbLink}`}
                      />
                    </Avatar>
                    <div>
                      <CardTitle style={{ marginBottom: 4 }}>
                        {user.Vorname} {user.Nachname}
                      </CardTitle>
                      <CardSubtitle>
                        <p style={{ fontSize: 12, fontWeight: 'bold' }}>
                          Rolle: {user.EmployeeRole}
                        </p>
                      </CardSubtitle>
                    </div>
                  </CardHeader>

                  <CardActions style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Button type="button" fillMode="flat" onClick={() => handleEdit(user)} svgIcon={pencilIcon} />

                    {/* --- Vorrübergehend deaktiviert! --- 
                    <Button type="button" fillMode="flat" onClick={() => handleDelete(user)} svgIcon={trashIcon} />
                    */}
                                        
                  </CardActions>
                </Card>
              </div>
            ))}
          </div>
        )}
      </div>

      <DeleteConfirmationModal
        visible={visibleDialog}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
      >
        Möchten Sie <strong>{selectedUser?.Vorname} {selectedUser?.Nachname}</strong> wirklich löschen?
        <br />
        <span style={{ fontSize: '10px', fontWeight: 'bold' }}>
          Hinweis: Diese Änderung kann nicht rückgängig gemacht werden!
        </span>
      </DeleteConfirmationModal>
    </div>
  );
};

export default Users;
